import type { AppError } from '../../core/error/appError';
import { isRetryable } from '../../core/error/appError';
import type { FieldError } from '../../core/validation/fieldError';
import { S } from '../../i18n/strings';

/**
 * Único punto donde un error deja de ser un valor y se convierte en texto.
 *
 * Mantenerlo aquí es lo que permite probar la clasificación de errores sin renderizar y
 * traducir la app sin tocar el dominio.
 */
export interface ErrorPresentation {
  readonly title: string;
  readonly body: string | null;
  /** Acción principal; `null` cuando reintentar no puede cambiar nada (409, 422). */
  readonly action: ErrorAction | null;
}

export type ErrorAction = 'RETRY' | 'CHANGE_TIME' | 'LOGIN' | 'DISMISS';

export function actionLabel(action: ErrorAction): string {
  switch (action) {
    case 'RETRY':
      return S.actionRetry;
    case 'CHANGE_TIME':
      return S.actionChangeTime;
    case 'LOGIN':
      return S.actionLogin;
    case 'DISMISS':
      return S.actionDismiss;
  }
}

export function presentError(error: AppError): ErrorPresentation {
  switch (error.kind) {
    case 'OFFLINE':
      return { title: S.errorNetworkTitle, body: S.errorOfflineBody, action: 'RETRY' };
    case 'NETWORK':
      return { title: S.errorNetworkTitle, body: S.errorNetworkBody, action: 'RETRY' };
    case 'TIMEOUT':
      return { title: S.errorTimeoutTitle, body: S.errorNetworkBody, action: 'RETRY' };
    case 'SERVER':
      return { title: S.errorServerTitle, body: S.errorServerBody, action: 'RETRY' };
    case 'RATE_LIMITED':
      return { title: S.errorRateLimitedTitle, body: S.errorServerBody, action: 'RETRY' };
    case 'NOT_FOUND':
      return { title: S.errorNotFoundTitle, body: null, action: null };
    case 'SESSION_EXPIRED':
      return { title: S.errorSessionExpiredTitle, body: S.errorSessionExpiredBody, action: 'LOGIN' };
    case 'FORBIDDEN':
      return { title: S.errorServerTitle, body: S.errorServerBody, action: null };
    // Reintentar un 409 no devuelve la mesa: la única salida es cambiar de horario.
    case 'SLOT_TAKEN':
      return { title: S.errorSlotTakenTitle, body: S.errorSlotTakenBody, action: 'CHANGE_TIME' };
    case 'VALIDATION':
      // El detalle se pinta bajo cada campo; aquí solo se enuncia que hay algo que revisar.
      return { title: S.errorValidationTitle, body: null, action: null };
    case 'UNKNOWN':
      return {
        title: S.errorUnknownTitle,
        body: error.cause,
        action: isRetryable(error) ? 'RETRY' : 'DISMISS',
      };
  }
}

export function fieldErrorMessage(error: FieldError): string {
  switch (error) {
    case 'NAME_REQUIRED':
      return S.validationNameRequired;
    case 'NAME_LENGTH':
      return S.validationNameLength;
    case 'PHONE_REQUIRED':
      return S.validationPhoneRequired;
    case 'PHONE_FORMAT':
      return S.validationPhoneFormat;
    case 'EMAIL_REQUIRED':
      return S.validationEmailRequired;
    case 'EMAIL_FORMAT':
      return S.validationEmailFormat;
    case 'DATE_PAST':
      return S.validationDatePast;
    case 'DATE_TOO_FAR':
      return S.validationDateTooFar;
    case 'TIME_PAST':
      return S.validationTimePast;
    case 'TIME_CLOSED':
      return S.validationTimeClosed;
    case 'PARTY_MIN':
      return S.validationPartyMin;
    case 'PARTY_MAX':
      return S.validationPartyMax;
    case 'NOTES_LENGTH':
      return S.validationNotesLength;
    case 'TERMS_REQUIRED':
      return S.validationTermsRequired;
    case 'UNKNOWN':
      return S.validationUnknown;
  }
}
